/**
 * Basic email shape check: a single "@" with non-empty, dot-containing domain
 * and no whitespace. Deliberately permissive — we only guard against obvious
 * typos, not RFC-perfect addresses. Used on Hasher create/edit when an email is
 * supplied (the field stays optional unless a membership is being recorded).
 */
export function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}
/**
 * The fields a hasher must have before a membership can be recorded for them
 * (nerdName and email — both optional in general, required here). Returns the
 * human-readable labels of whatever's missing/blank, in display order; an empty
 * array means the hasher is eligible. The api enforces this on record and the
 * web uses it to gate the "Record dues" affordance, so the two never disagree.
 */
export function membershipMissingFields(hasher) {
    const missing = [];
    if (!hasher.nerdName?.trim())
        missing.push("nerd name");
    if (!hasher.email?.trim())
        missing.push("email");
    return missing;
}
/**
 * A kennel's display name: its short name when set, else the full name. This is
 * the single source of truth for the name cached onto Trail.kennel at write
 * time and shown in trail lists. shortName is trimmed; an empty/whitespace one
 * falls back to the full name.
 */
export function kennelDisplayName(k) {
    const short = k.shortName?.trim();
    return short ? short : k.name;
}
/**
 * The hare listing to display for a trail/event. A non-empty custom hare
 * listing (free text, e.g. off-system or legacy hares) replaces the resolved
 * hasher hare names entirely; otherwise the resolved names are used. Returns []
 * when neither is set, so the caller can show "TBD". Single source of truth for
 * the "custom hares displayed instead" rule, shared by api and web.
 */
export function displayHares(t) {
    const custom = t.customHare?.trim();
    if (custom)
        return [custom];
    return t.hareNames ?? [];
}
// Self check-in window (Story 13.1): 12h before to 6h after the trail's
// scheduled start. `startAt` embeds the trail's local offset, so a plain parse
// is zone-correct. Date-only trails have no start instant and no window.
const WINDOW_BEFORE_MS = 12 * 60 * 60 * 1000;
const WINDOW_AFTER_MS = 6 * 60 * 60 * 1000;
/** UTC epoch-ms of a trail's scheduled start; null for date-only trails. */
export function eventStartMs(trail) {
    if (!trail.startAt)
        return null;
    const ms = Date.parse(trail.startAt);
    return Number.isFinite(ms) ? ms : null;
}
/** True if `nowMs` is within the trail's [start-12h, start+6h] window. */
export function isWithinCheckInWindow(trail, nowMs) {
    const start = eventStartMs(trail);
    if (start === null)
        return false;
    return nowMs >= start - WINDOW_BEFORE_MS && nowMs <= start + WINDOW_AFTER_MS;
}
// Hare attendance window: a hare can take attendance / mark paid from local
// midnight on trail day through 48h after the trail starts.
const HARE_WINDOW_AFTER_START_MS = 48 * 60 * 60 * 1000;
/**
 * The UTC offset (ms) embedded in an ISO 8601 instant, e.g.
 * "2026-06-10T19:00:00-04:00" → -4h. 0 for a trailing Z; null when absent or
 * unparseable.
 */
export function utcOffsetMs(instant) {
    if (!instant)
        return null;
    const m = /(?:Z|([+-])(\d{2}):?(\d{2}))$/.exec(instant.trim());
    if (!m)
        return null;
    if (!m[1])
        return 0; // trailing Z
    const sign = m[1] === "-" ? -1 : 1;
    return sign * (Number(m[2]) * 60 + Number(m[3])) * 60 * 1000;
}
/**
 * True if `nowMs` falls in a trail's hare-attendance window: opens at local
 * midnight on trail day (in the trail's own timezone, from the offset embedded
 * in `startAt`) and closes 48h after the trail starts. This is the single
 * source of truth for that window — the api gates writes with it
 * (`canTakeAttendanceOn`) and the web only offers the manage-check-in
 * affordance when it's open, so the two never disagree. Legacy date-only trails
 * (no `startAt`) fall back to local midnight (UTC) for both the start instant
 * and the timezone.
 */
export function isWithinHareAttendanceWindow(trail, nowMs) {
    const offsetMs = utcOffsetMs(trail.startAt) ?? 0;
    // Local midnight on trail day, expressed as a UTC epoch.
    const opensAt = Date.parse(`${trail.date}T00:00:00Z`) - offsetMs;
    // Precise start instant; legacy date-only trails fall back to local midnight.
    const parsed = trail.startAt ? Date.parse(trail.startAt) : NaN;
    const startAt = Number.isNaN(parsed) ? opensAt : parsed;
    const closesAt = startAt + HARE_WINDOW_AFTER_START_MS;
    return nowMs >= opensAt && nowMs < closesAt;
}
/** True for a special event (Milestone 9); false for a numbered trail. */
export function isSpecialEvent(event) {
    return event.eventType === "special";
}
/**
 * The short display label for an event: its name for a special event, else
 * "#<number>" for a numbered trail. The kennel name is rendered separately by
 * callers (e.g. "Hash NYC #1742" or "Hash NYC Red Dress Run"). Centralized so
 * every screen labels trails and special events the same way.
 */
export function eventLabel(event) {
    if (isSpecialEvent(event))
        return event.name?.trim() || "Special event";
    return `#${event.number}`;
}
/**
 * Suggest the trail number for a new trail in a kennel on a target `date`, by
 * slotting it chronologically into the kennel's existing numbered trails: the
 * highest number among trails dated on or before `date`, plus one. When `date`
 * precedes every existing trail, suggest one below the earliest (clamped to 1).
 * Returns undefined when the kennel has no numbered trails yet — the misman
 * types the first number. Special events (no number) are ignored.
 *
 * `trails` should already be the target kennel's trails; callers filter by
 * kennelId before calling.
 */
export function predictTrailNumber(trails, date) {
    const numbered = trails.filter((t) => !isSpecialEvent(t) && typeof t.number === "number");
    if (numbered.length === 0)
        return undefined;
    const onOrBefore = numbered.filter((t) => t.date <= date);
    if (onOrBefore.length > 0) {
        return Math.max(...onOrBefore.map((t) => t.number)) + 1;
    }
    return Math.max(1, Math.min(...numbered.map((t) => t.number)) - 1);
}
// System-wide what-to-bring items a hare can toggle on a trail (Story 7.1).
// Stored on the trail by key; custom items are free text alongside.
export const STANDARD_BRING_ITEMS = [
    { key: "vessel", label: "Vessel" },
    { key: "lamp", label: "Cranium lamp" },
    { key: "cash", label: "Cash" },
    { key: "transit", label: "Transit card" },
];
/**
 * Membership state is DERIVED, never stored (Story 10.2):
 *   active — some paid period interval contains today
 *   lapsed — paid intervals exist but none contains today
 *   unpaid — no membership record / no paid intervals at all
 * `today` is a YYYY-MM-DD string (the trail/event's local day or the server's).
 */
export function membershipState(membership, today) {
    const periods = membership?.periods ?? [];
    if (periods.length === 0)
        return "unpaid";
    return periods.some((p) => p.start <= today && today <= p.end)
        ? "active"
        : "lapsed";
}
/**
 * The door amount owed by a hasher for a trail, in minor units.
 * Standard trail: active members are free; everyone else pays the kennel door
 * fee (0 if unset). Special-fee trail (Story 7.1): everyone pays — active
 * members the member price, everyone else the non-member price.
 *
 * `opts.virgin` + `opts.virginsFreeDoor` (the kennel toggle) comp a first-timer
 * the standard door fee — their first trail is free. This applies only to the
 * standard door path: special-fee events still charge everyone (the toggle sits
 * next to the door fee and waives that fee, not event pricing).
 */
export function doorAmountOwed(state, doorFee, trail, opts) {
    if (trail?.specialFee) {
        return (state === "active" ? trail.memberPrice : trail.nonMemberPrice) ?? 0;
    }
    if (state === "active")
        return 0;
    if (opts?.virgin && opts.virginsFreeDoor)
        return 0;
    return doorFee ?? 0;
}
/**
 * Add calendar months to a YYYY-MM-DD date, clamping the day-of-month so e.g.
 * Jan 31 + 1 month -> Feb 28/29 rather than rolling into March. Handles year
 * rollover (Dec + 1 -> Jan of the next year).
 */
export function addMonthsClamped(ymd, months) {
    const [y, m, d] = ymd.split("-").map(Number);
    const targetMonthIndex = m - 1 + months; // 0-based
    const targetYear = y + Math.floor(targetMonthIndex / 12);
    const monthInYear = ((targetMonthIndex % 12) + 12) % 12;
    const lastDay = new Date(Date.UTC(targetYear, monthInYear + 1, 0)).getUTCDate();
    const day = Math.min(d, lastDay);
    return new Date(Date.UTC(targetYear, monthInYear, day))
        .toISOString()
        .slice(0, 10);
}
/** The day before a YYYY-MM-DD date. */
export function dayBefore(ymd) {
    const [y, m, d] = ymd.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d - 1)).toISOString().slice(0, 10);
}
/** The date `days` days before a YYYY-MM-DD date. */
export function daysBefore(ymd, days) {
    const [y, m, d] = ymd.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d - days)).toISOString().slice(0, 10);
}
/**
 * The definite date periods a scheme's purchase window has opened for, as of
 * `today` (Story 10.4): the window opens `windowDays` before a period starts,
 * and the period stays purchasable until it ends — so a lapsed member can
 * still pay at the door mid-period. Ordered by start date.
 *
 * For a recurrence rule, period k runs from anchor+k*months through the day
 * before anchor+(k+1)*months (day-of-month clamped). For an explicit list,
 * the periods are taken as defined.
 */
export function purchasablePeriods(scheme, today) {
    const open = (p) => daysBefore(p.start, scheme.windowDays) <= today && today <= p.end;
    if (scheme.periods) {
        return [...scheme.periods]
            .sort((a, b) => a.start.localeCompare(b.start))
            .filter(open);
    }
    if (!scheme.recurrence)
        return [];
    const { months, anchor } = scheme.recurrence;
    const result = [];
    for (let k = 0;; k++) {
        const start = addMonthsClamped(anchor, k * months);
        // Stop once this period's window hasn't opened yet — later ones are
        // strictly further out. (The series is unbounded; only windows-open
        // periods ever materialize.)
        if (daysBefore(start, scheme.windowDays) > today)
            break;
        const end = dayBefore(addMonthsClamped(anchor, (k + 1) * months));
        if (end >= today)
            result.push({ start, end });
    }
    return result;
}
